import { apiConfigured, clientDigitalTwin } from '../api.js'
import { clearClientSession, getClientSession, getClientToken, isClientLoggedIn } from '../clientAuth.js'
import { getCachedPortalDashboard } from '../portalDashboardCache.js'
import { portalDashboardHasTwin, resolvePortalActiveInstallationId } from '../portalInstallContext.js'

export function renderTwin() {
  return `
    <div class="portal-card portal-hero dash-hero">
      <div>
        <h1>Gemelo 3D</h1>
        <p class="portal-muted" id="twin-subtitle">Solo lectura · modelo creado en ITManager</p>
      </div>
      <a href="/mi-instalacion" data-link class="portal-btn">Volver al dashboard</a>
    </div>
    <div class="portal-card twin-card">
      <canvas id="twin-canvas" width="960" height="540"></canvas>
      <p id="twin-msg" class="portal-muted portal-form-msg" role="status"></p>
    </div>
  `
}

export function bindTwin(root, { navigate }) {
  if (!isClientLoggedIn()) {
    navigate('/cliente', { replace: true })
    return
  }
  const msg = root.querySelector('#twin-msg')
  const subtitle = root.querySelector('#twin-subtitle')
  const canvas = root.querySelector('#twin-canvas')
  const session = getClientSession()
  const installationId = resolvePortalActiveInstallationId(session)
  const cached = getCachedPortalDashboard(installationId, getClientToken())
  if (cached && !portalDashboardHasTwin(cached)) {
    navigate('/mi-instalacion', { replace: true })
    return
  }

  ;(async () => {
    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible.'
      return
    }
    msg.textContent = 'Cargando modelo…'
    try {
      const data = await clientDigitalTwin(installationId)
      if (!data.ok || data.has_twin === false) {
        msg.textContent = data.message || 'Sin gemelo disponible.'
        navigate('/mi-instalacion', { replace: true })
        return
      }
      subtitle.textContent = `${data.installation?.name || ''} · ${data.asset?.name || 'Gemelo'}`
      const blocks = data.asset?.meta?.blocks || []
      renderBlocks(canvas, blocks, data.heat_map || [])
      msg.textContent = blocks.length
        ? `${blocks.length} bloques · salud ${data.health_score ?? '—'}`
        : 'Modelo vacío (aún no diseñado en ITManager).'
    } catch (e) {
      if (e.status === 401) {
        clearClientSession()
        navigate('/cliente', { replace: true })
        return
      }
      // 404 / sin twin → volver al dashboard (no dejar ruta rota).
      if (e.status === 404) {
        navigate('/mi-instalacion', { replace: true })
        return
      }
      msg.textContent = e.message || 'No se pudo cargar el gemelo.'
    }
  })()
}

function renderBlocks(canvas, blocks, heatMap) {
  const ctx = canvas.getContext('2d')
  const w = canvas.width
  const h = canvas.height
  ctx.fillStyle = '#1a1d23'
  ctx.fillRect(0, 0, w, h)

  const heat = new Map((heatMap || []).map((z) => [z.zone_id, z.traffic_light]))
  const colorFor = (zoneId) => {
    const t = heat.get(zoneId)
    if (t === 'green') return '#22c55e'
    if (t === 'yellow') return '#eab308'
    if (t === 'red') return '#ef4444'
    return '#4b9cff'
  }

  const originX = w / 2
  const originY = h * 0.72
  const scale = 18
  const project = (x, y, z) => {
    const isoX = (x - z) * scale * 0.866
    const isoY = (x + z) * scale * 0.5 - y * scale
    return [originX + isoX, originY + isoY]
  }

  const list =
    Array.isArray(blocks) && blocks.length
      ? blocks
      : [
          { w: 4, h: 3, d: 4, x: -3, y: 0, z: 0, zone_id: 'z1' },
          { w: 3, h: 4, d: 3, x: 2, y: 0, z: -1, zone_id: 'z2' },
        ]

  for (const b of list) {
    drawBox(
      ctx,
      project,
      Number(b.x) || 0,
      Number(b.y) || 0,
      Number(b.z) || 0,
      Number(b.w) || 2,
      Number(b.h) || 2,
      Number(b.d) || 2,
      colorFor(b.zone_id)
    )
  }
}

function drawBox(ctx, project, x, y, z, bw, bh, bd, color) {
  const p = [
    project(x, y, z),
    project(x + bw, y, z),
    project(x + bw, y, z + bd),
    project(x, y, z + bd),
    project(x, y + bh, z),
    project(x + bw, y + bh, z),
    project(x + bw, y + bh, z + bd),
    project(x, y + bh, z + bd),
  ]
  poly(ctx, [p[4], p[5], p[6], p[7]], shade(color, 1.15))
  poly(ctx, [p[0], p[3], p[7], p[4]], shade(color, 0.75))
  poly(ctx, [p[1], p[2], p[6], p[5]], shade(color, 0.9))
}

function poly(ctx, pts, fill) {
  ctx.beginPath()
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
  ctx.closePath()
  ctx.fillStyle = fill
  ctx.fill()
  ctx.strokeStyle = 'rgba(0,0,0,.35)'
  ctx.stroke()
}

function shade(hex, factor) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return hex
  const ch = (i) => Math.min(255, Math.max(0, Math.round(parseInt(m[i], 16) * factor)))
  return `rgb(${ch(1)},${ch(2)},${ch(3)})`
}
